import { FC, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { HOME_ROUTE, NEW_CAMPAIGN_ROUTE } from 'src/constants/routes'
import en from 'src/constants/en'
import { useGetCampaignsQuery } from 'src/reactQuery/useGetCampaignsQuery'
import { useGetCampaignsOverviewQuery } from 'src/reactQuery/useGetCampaignsOverviewQuery'
import { useGetAllTopSourcesQuery } from 'src/reactQuery/useGetAllTopSourcesQuery'
import { CampaignPayload, CampaignType } from 'src/services/__generated__/api'
import { useCustomLink } from 'src/hooks/useCustomLink'
import { cn } from 'src/utils/cn'
import {
  CampaignDetailHeader,
  CampaignDetailView,
} from 'src/pages/ThemeDetail/CampaignDetailHeader'

import { AddNewCard } from 'src/components/AddNewCard'
import { BaseModal } from 'src/containers/BaseModal'
import { ConfirmationModalContent } from 'src/components/ConfirmationModalContent'
import { AnalyticsApi } from 'src/services/analyticsApi'
import { TabsSection } from 'src/pages/AIListening/Main/TabsSection'
import { useQueryParams } from 'src/hooks/useQueryParams'
import { FilterSelectOption } from 'src/components/FilterSelect/interfaces/filterSelectOption.interface'

import { useUpdateCampaignMutation } from 'src/reactQuery/useUpdateCampaignMutation'
import { useCreateCampaignMutation } from 'src/reactQuery/useCreateCampaignMutation'
import { getDateRangeByQueryParams } from 'src/utils/getDateRangeByQueryParams'
import { QueryParams } from 'src/pages/Campaigns/interfaces/queryParams.interface'
import { TopicsOverviewSection } from 'src/charts/TopicsSubtopics/TopicsOverviewSection'
import { useGetTopicsOverviewForCampaignsQuery } from 'src/reactQuery/useGetSocialTopicsQuery'
import { CampaignComparison } from './CampaignComparison'

import CampaignsFilter from './CampaignsFilter'
import { FilteredCampaignsByCategory } from './interfaces/filteredCampaignsByCategory.interface'
import { sortFilteredDataByOrder } from './utils/sortFilteredDataByOrder'
import { ORDER_BY } from './constants/orderBy'
import { sortCategoryObjects } from './utils/sortCategoryObjects'
import { getCategoriesOptions } from './utils/getCategoriesOptions'
import { groupCampaignsByCategories } from './utils/groupCampaignsByCategories'
import CampaignCard from './CampaignCard'
import styles from './campaigns.module.scss'
import { RenameCampaignModalContent } from './RenameCampaignModalContent'
import { HeroSection } from './HeroSection'
import { DownloadReportModal } from './DownloadReportModal'

const COPY = {
  // Plain "Overview" here: on the campaigns list there is no campaign detail to
  // contrast a "General Overview" against.
  overview: 'Overview',
  title: 'Campaigns',
}

export const Campaigns: FC = () => {
  const navigate = useNavigate()

  const { queryParams, updateQueryParams } = useQueryParams<QueryParams>({
    qsStringifyOptions: {
      arrayFormat: 'comma',
    },
  })

  const dates = getDateRangeByQueryParams(queryParams)

  const startDate = dates?.start

  const endDate = dates?.end

  const selectedCampaigns = queryParams.campaigns || []

  // `campaigns` is serialised comma-style, so qs hands it back as one string;
  // indexing that would take its first character, not the first id.
  const topicsCampaignIds = useMemo(
    () =>
      (Array.isArray(selectedCampaigns)
        ? selectedCampaigns
        : String(selectedCampaigns).split(',')
      ).filter(Boolean),
    [selectedCampaigns]
  )

  // Topics are tagged per campaign; the overview merges every campaign in
  // view by topic name so the card describes the whole selection.
  const {
    data: topicsOverview,
    isError: topicsOverviewError,
    isLoading: topicsOverviewLoading,
  } = useGetTopicsOverviewForCampaignsQuery(topicsCampaignIds, {
    fromDate: startDate,
    toDate: endDate,
  })

  const [campaignToDelete, setCampaignToDelete] = useState<CampaignType | null>(
    null
  )

  const [campaignToRename, setCampaignToRename] = useState<CampaignType | null>(
    null
  )

  const [campaignToReactivate, setCampaignToReactivate] =
    useState<CampaignType | null>(null)

  const [downloadReportOpen, setDownloadReportOpen] = useState(false)
  // Overview or Campaign Comparison — several campaigns over one window, the
  // mirror of Data Comparison on a campaign's own page.
  const [view, setView] = useState<CampaignDetailView>('general')

  const { data: campaignsData, refetch: campaignsRefetch } =
    useGetCampaignsQuery({
      end: endDate,
      start: startDate,
    })

  const {
    data: campaignsOverviewData,
    isFetching: campaignsOverviewIsFetching,
  } = useGetCampaignsOverviewQuery({
    end: endDate,
    start: startDate,
  })

  const { data: topSourcesData } = useGetAllTopSourcesQuery({
    end: endDate,
    start: startDate,
  })

  const uniqueSourcesList = useMemo<string[]>(() => {
    const all =
      campaignsData?.campaigns?.flatMap(
        (campaign) => campaign.stats?.sources?.map((s) => s.source) || []
      ) || []
    return Array.from(new Set(all)).filter(Boolean) as string[]
  }, [campaignsData?.campaigns])

  const mutation = useUpdateCampaignMutation({
    onSuccess() {
      campaignsRefetch()
    },
    updateType: 'update',
  })

  const duplicateMutation = useCreateCampaignMutation({
    onSuccess: () => {
      campaignsRefetch()
    },
  })

  const campaignOptions = useMemo<FilterSelectOption[]>(() => {
    return (
      campaignsData?.campaigns?.map((item, index) => ({
        label: item.name || '',
        value: item._id || index.toString(),
      })) || []
    )
  }, [campaignsData?.campaigns])

  const selectedCampaignOptions = useMemo<FilterSelectOption[]>(() => {
    return campaignOptions.filter((item) =>
      selectedCampaigns.includes(item.value)
    )
  }, [campaignOptions, selectedCampaigns])

  const [filteredData, setFilteredData] = useState<
    FilteredCampaignsByCategory[] | null
  >(null)

  const [activeNum, setActiveNum] = useState<number>(0)

  const [draftNum, setDraftNum] = useState<number>(0)

  const [completedNum, setCompletedNum] = useState<number>(0)

  const [allNum, setAllNum] = useState<number>(0)

  const [extractedCategories, setExtractedCategories] =
    useState<Array<string> | null>(null)

  const [filterOpen, setFilterOpen] = useState(false)

  const [orderFilter, setOrderFilter] = useState<string>(ORDER_BY[2])

  const [categoriesFilterArr, setCategoriesFilterArr] = useState<Array<any>>([])

  const [searchInput, setSearchInput] = useState<string>('')

  const backURL = useCustomLink(HOME_ROUTE)

  const [statusFilter, setStatusFilter] = useState<
    'saved' | 'archived' | 'completed' | 'all'
  >('saved')

  const createCategoryFilterOptions = () => {
    if (campaignsData?.campaigns) {
      setExtractedCategories(getCategoriesOptions(campaignsData.campaigns))
    }
  }

  const handleDelete = async (id: string): Promise<void> => {
    try {
      await AnalyticsApi.v1Private.campaignDelete(id)

      campaignsRefetch()
    } catch (error) {
      toast.error(en.somethingWentWrong)
    }
  }

  const handleCampaignUpdate = async (
    campaign: CampaignPayload,
    id: string
  ) => {
    const payload: CampaignPayload = {
      from: campaign?.from,
      keywords: campaign?.keywords,
      name: campaign?.name,
      status: campaign?.status,
      targets: campaign?.targets,
      to: campaign?.to,
    }

    mutation.mutate({ data: payload, id })
  }

  const handleDuplicate = (campaign: CampaignType) => {
    const payload = {
      from: campaign?.from ?? '',
      irrelevant_keywords: campaign?.irrelevant_keywords || [],
      keywords: campaign?.keywords || [],
      name: `${campaign?.name} (Copy)`,
      status: 'active',
      tags: campaign?.tags || [],
      targets: campaign?.targets || [],
      timezone: campaign?.timezone,
      timezoneName: campaign?.timezoneName,
      to: campaign?.to ?? '',
    } as any

    // Add target and exclude link values dynamically
    campaign?.targets?.forEach((target) => {
      const targetKey = `target_${target === 'Facebook' ? 'fb' : target}_pages`
      const excludeKey = `exclude_${target === 'Facebook' ? 'fb' : target}_pages`

      if ((campaign as any)[targetKey]) {
        payload[targetKey] = (campaign as any)[targetKey]
      }

      if ((campaign as any)[excludeKey]) {
        payload[excludeKey] = (campaign as any)[excludeKey]
      }
    })

    duplicateMutation.mutate(payload)
  }

  const handleReactivate = (campaign: CampaignType) => {
    setCampaignToReactivate(campaign)
  }

  useEffect(() => {
    createCategoryFilterOptions()

    const activeArr = campaignsData?.campaigns?.filter((item) => {
      return item.status?.toLowerCase() === 'active'
    })
    const draftArr = campaignsData?.campaigns?.filter((item) => {
      return item.status?.toLowerCase() === 'draft'
    })
    const completedArr = campaignsData?.campaigns?.filter((item) => {
      return item.status?.toLowerCase() === 'completed'
    })

    setActiveNum(activeArr?.length ?? 0)
    setDraftNum(draftArr?.length ?? 0)
    setCompletedNum(completedArr?.length ?? 0)
    setAllNum(campaignsData?.campaigns?.length ?? 0)
  }, [campaignsData])

  const filterThemes = () => {
    let filtered

    if (statusFilter === 'all') {
      filtered = campaignsData?.campaigns?.filter((item) => {
        return item?.name?.toLowerCase()?.includes(searchInput.toLowerCase())
      })
    } else if (statusFilter === 'saved') {
      filtered = campaignsData?.campaigns?.filter((item) => {
        return (
          item?.status?.toLowerCase() === 'active' &&
          item?.name?.toLowerCase()?.includes(searchInput.toLowerCase())
        )
      })
    } else if (statusFilter === 'archived') {
      filtered = campaignsData?.campaigns?.filter((item) => {
        return (
          item?.status?.toLowerCase() === 'draft' &&
          item?.name?.toLowerCase()?.includes(searchInput.toLowerCase())
        )
      })
    } else {
      filtered = campaignsData?.campaigns?.filter((item) => {
        return (
          item?.status?.toLowerCase() === statusFilter.toLowerCase() &&
          item?.name?.toLowerCase()?.includes(searchInput.toLowerCase())
        )
      })
    }

    const grouped = groupCampaignsByCategories(filtered || [])

    const temp: [string, CampaignType[]][][] = []
    categoriesFilterArr.slice().forEach((selectedCategory) => {
      const selectedByKey = Object.entries(grouped).filter(
        ([key]) => key === selectedCategory
      )
      if (selectedByKey.length) {
        temp.push(selectedByKey)
      }
    })
    const withoutCategory = Object.entries(grouped).filter(
      ([key]) => key === 'Themes With No Categories'
    )
    if (withoutCategory.length) {
      temp.push(withoutCategory)
    }

    const finalData = temp.map((item) =>
      item.reduce<{ [key: string]: CampaignType[] }>((accum, [k, v]) => {
        // eslint-disable-next-line no-param-reassign
        accum[k] = v

        return accum
      }, {})
    )

    sortCategoryObjects(finalData)

    sortFilteredDataByOrder(finalData, orderFilter)

    setFilteredData(finalData)
  }

  const handleChangeOrderFilter = (val: string) => {
    setOrderFilter(val)
  }

  const handleChangeCategoriesFilter = (val: string[]) => {
    setCategoriesFilterArr(val)
  }

  const handleSearchChange = (val: string) => {
    setSearchInput(val)
  }

  useEffect(() => {
    if (campaignsData) {
      filterThemes()
    }
  }, [
    campaignsData,
    categoriesFilterArr,
    orderFilter,
    statusFilter,
    searchInput,
  ])

  useEffect(() => {
    campaignsRefetch()
  }, [startDate, endDate])

  useEffect(() => {
    if (campaignOptions.length) {
      updateQueryParams({
        campaigns: selectedCampaigns.length
          ? selectedCampaigns
          : campaignOptions.map((item) => item.value),
      })
    }
  }, [campaignOptions, selectedCampaigns])

  // Only what the modal needs: an id and a name per campaign.
  const comparisonCampaigns = (campaignsData?.campaigns ?? []).map(
    (campaign) => ({
      id: String(campaign._id),
      name: campaign.name ?? '',
    })
  )

  if (view === 'comparison') {
    return (
      <div className={cn(styles.campaigns, styles.campaignsComparison)}>
        <CampaignComparison
          backUrl={backURL}
          campaigns={comparisonCampaigns}
          onViewChange={setView}
          view={view}
        />
      </div>
    )
  }

  return (
    <div className={styles.campaigns}>
      {/* The same header the campaign pages use, so the Overview / Data
          Comparison toggle sits in one place across the feature.
          showActions={false}, not just a missing `action`: the action prop only
          OVERRIDES the Edit Campaign button, so dropping it left the header
          showing Edit Campaign on a page that has no one campaign to edit.
          The page offers what it needs further down anyway — Download Mention
          Reports beside the list it downloads, Add New Campaign as a card in
          the grid it adds to. */}
      <CampaignDetailHeader
        backUrl={backURL}
        campaignName={COPY.title}
        className={styles.overviewHeader}
        generalLabel={COPY.overview}
        onViewChange={setView}
        parent="Social Listening"
        showActions={false}
        view={view}
      />

      <HeroSection
        isLoading={campaignsOverviewIsFetching && !campaignsOverviewData}
        overview={campaignsOverviewData?.data}
        platformSentimentData={(topSourcesData ?? []).map((source) => ({
          negative: source.negatives ?? 0,
          neutral: source.neutrals ?? 0,
          platform: source.name ?? 'Unknown',
          positive: source.positives ?? 0,
        }))}
        sourcesList={uniqueSourcesList}
        topSourcesData={topSourcesData ?? []}
      />

      {/* Topics are campaign-scoped, so the overview shows the first selected
          campaign. With none selected there is nothing to summarise. */}
      <TopicsOverviewSection
        data={topicsOverview}
        isError={topicsOverviewError}
        isLoading={topicsOverviewLoading}
      />

      <TabsSection
        activeTab={statusFilter}
        allCount={allNum}
        archivedCount={draftNum}
        completedCount={completedNum}
        onDownloadReport={() => setDownloadReportOpen(true)}
        onFilterClick={() => setFilterOpen(!filterOpen)}
        onSearchChange={handleSearchChange}
        onTabChange={setStatusFilter}
        savedCount={activeNum}
        searchValue={searchInput}
      />

      <CampaignsFilter
        campaignOptions={campaignOptions}
        categoriesData={extractedCategories || undefined}
        open={filterOpen}
        orderCallback={handleChangeOrderFilter}
        orderOptions={ORDER_BY}
        selectedCampaignOptions={selectedCampaignOptions}
        themesCallback={handleChangeCategoriesFilter}
      />

      <div className={styles.cards}>
        {filteredData?.length ? (
          <>
            <div className={styles.cardRow}>
              {filteredData
                .flatMap((categoryObj) => {
                  const [, campaigns] = Object.entries(categoryObj).at(0) ?? []
                  return campaigns ?? []
                })
                .filter(
                  (campaign) =>
                    !campaign?._id || selectedCampaigns.includes(campaign?._id)
                )
                .map((campaign) => (
                  <CampaignCard
                    campaignRefetch={campaignsRefetch}
                    data={campaign}
                    key={campaign._id}
                    onDelete={() => setCampaignToDelete(campaign)}
                    onDuplicate={() => handleDuplicate(campaign)}
                    onReactivate={() => handleReactivate(campaign)}
                    onRename={() => setCampaignToRename(campaign)}
                  />
                ))}
              {statusFilter === 'saved' && (
                <AddNewCard
                  handleClick={() => navigate(NEW_CAMPAIGN_ROUTE)}
                  text={en.addNewCampaign}
                />
              )}
            </div>
          </>
        ) : statusFilter === 'saved' ? (
          <div className={styles.cardRow}>
            <AddNewCard
              handleClick={() => navigate(NEW_CAMPAIGN_ROUTE)}
              text={en.addNewCampaign}
            />
          </div>
        ) : (
          <div className={styles.empty}>{en.noCampaignsFound}</div>
        )}
      </div>

      <BaseModal isOpen={!!campaignToDelete}>
        <ConfirmationModalContent
          onCancel={() => setCampaignToDelete(null)}
          onConfirm={() => {
            if (campaignToDelete?._id) {
              handleDelete(campaignToDelete?._id)

              setCampaignToDelete(null)
            }
          }}
          title={`${en.areYouSureYouWantToDelete} ${campaignToDelete?.name}?`}
        />
      </BaseModal>

      <BaseModal isOpen={!!campaignToRename}>
        <RenameCampaignModalContent
          name={campaignToRename?.name}
          onCancel={() => setCampaignToRename(null)}
          onConfirm={(name) => {
            if (campaignToRename?._id) {
              handleCampaignUpdate(
                { ...campaignToRename, name },
                campaignToRename?._id
              )

              setCampaignToRename(null)
            }
          }}
        />
      </BaseModal>

      <BaseModal isOpen={!!campaignToReactivate}>
        <ConfirmationModalContent
          onCancel={() => setCampaignToReactivate(null)}
          onConfirm={() => {
            if (campaignToReactivate?._id) {
              handleCampaignUpdate(
                { ...campaignToReactivate, status: 'active' },
                campaignToReactivate?._id
              )

              setCampaignToReactivate(null)
            }
          }}
          title={`Reactivate "${campaignToReactivate?.name}"?`}
        />
      </BaseModal>

      {downloadReportOpen && (
        <DownloadReportModal
          campaigns={campaignsData?.campaigns ?? []}
          onClose={() => setDownloadReportOpen(false)}
        />
      )}
    </div>
  )
}
